import { omit, pick, uniq } from 'lodash-es'
import { assertString } from '#app/lib/assert_types'
import { isNonEmptyArray } from '#app/lib/boolean_tests'
import { getWikimediaLanguageCodeFromWdUri } from '#app/lib/languages/languages_indexes'
import { treq } from '#app/lib/treq'
import { objectKeys } from '#app/lib/utils'
import type { EntityDraft } from '#app/types/entity'
import { getClaimsValuesFromClaimsEditors, getPropertyClaimsCountFromEditors } from '#entities/components/editor/lib/editors_helpers'
import { getWorkPreferredAuthorRolesProperties } from '#entities/lib/editor/properties_per_subtype'
import { priorityPropertiesPerType, propertiesPerType, type CustomPropertyConfigByProperty } from '#entities/lib/editor/properties_per_type'
import { propertiesEditorsConfigs } from '#entities/lib/properties'
import { typeHasName } from '#entities/lib/types/entities_types'
import type { PropertyUri, WdEntityUri } from '#server/types/entity'
import type { EditionSeed, EntitySeed, ResolverEntry, WorkSeed } from '#server/types/resolver'
import { I18n } from '#user/lib/i18n'

interface GetMissingRequiredPropertiesParams {
  entity: EntityDraft
  requiredProperties: PropertyUri[]
  requiresLabel?: boolean
}

interface MissingRequiredLabel {
  key: 'label'
  labelAttributeName: string
}

interface MissingRequiredClaim {
  key: PropertyUri
  property: PropertyUri
  label: string
}

export type MissingRequiredAttribute = MissingRequiredLabel | MissingRequiredClaim

export function getMissingRequiredLabelOrClaims ({ entity, requiredProperties, requiresLabel }: GetMissingRequiredPropertiesParams) {
  const { type } = entity
  assertString(type)
  const missingRequiredAttributes: MissingRequiredAttribute[] = []
  if (requiresLabel) {
    if (objectKeys(entity.labels).length <= 0) {
      if (typeHasName(type)) {
        missingRequiredAttributes.push({ key: 'label', labelAttributeName: I18n('name') })
      } else {
        missingRequiredAttributes.push({ key: 'label', labelAttributeName: I18n('title') })
      }
    }
  }
  for (const property of requiredProperties) {
    const propertyClaimsCount = getPropertyClaimsCountFromEditors(entity.claimsEditors[property])
    if (propertyClaimsCount <= 0) {
      const labelKey = propertiesPerType[type][property].customLabel || property
      missingRequiredAttributes.push({ key: property, property, label: I18n(labelKey) })
    }
  }
  return missingRequiredAttributes
}

export async function createEditionAndWorkFromEntry (edition: EditionSeed & EntityDraft, work: WorkSeed & EntityDraft) {
  edition.claims = getClaimsValuesFromClaimsEditors(edition.claimsEditors)
  work.claims = getClaimsValuesFromClaimsEditors(work.claimsEditors)
  const title = edition.claims['wdt:P1476'][0]
  const titleLang = edition.claims['wdt:P407'][0] as WdEntityUri
  const workLabelLangCode = await getWikimediaLanguageCodeFromWdUri(titleLang) || 'en'
  work.labels = { [workLabelLangCode]: title }
  const entry: ResolverEntry = {
    edition: omit(edition, 'claimsEditors') as EntitySeed,
    works: [ omit(work, 'claimsEditors') ],
  }
  return resolveAndGetEditionUri(entry)
}

export async function resolveAndGetEditionUri (entry: ResolverEntry) {
  const { entries } = await treq('entities', 'post', 'resolve', {
    entries: [ entry ],
    update: true,
    create: true,
    enrich: true,
  })
  const { uri } = entries[0].edition
  return uri
}

export function getPropertiesShortlist (entity: EntityDraft) {
  const { type, claims } = entity
  const typeShortlist = priorityPropertiesPerType[type]

  const claimsProperties = objectKeys(claims)
    .filter(isShortlistableProperty({ claims, type }))

  const propertiesShortlist = uniq([ ...typeShortlist, ...claimsProperties ])
  // If a serie was passed in the claims, invite to add an ordinal
  if (claimsProperties.includes('wdt:P179')) propertiesShortlist.push('wdt:P1545')
  const authorProperties = getWorkPreferredAuthorRolesProperties(entity)
  return uniq(propertiesShortlist.flatMap(property => {
    if (property === 'wdt:P50') return authorProperties
    else return [ property ]
  })) as PropertyUri[]
}

const isShortlistableProperty = ({ claims, type }) => (property: PropertyUri) => {
  const values = claims[property]
  if (!isNonEmptyArray(values)) return false

  // Some properties might not have an editor
  const datatype = propertiesEditorsConfigs[property]?.datatype
  if (!datatype) return false

  if (propertiesPerType[type][property] == null) return false

  // Filter-out fixed editor: 'fixed-entity', 'fixed-string'
  if (datatype.split('-')[0] === 'fixed') return false

  return true
}

export function removeNonTypeProperties (claims: EntityDraft['claims'], typeProperties: CustomPropertyConfigByProperty) {
  const typePropertiesIds = objectKeys(typeProperties)
  return pick(claims, typePropertiesIds)
}
